import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/client';
import { queryKeys } from './queryKeys';
import useProtectedQueryEnabled from './useProtectedQueryEnabled';

export const PAYMENT_STATUS_MAX_POLL_MS = 15 * 60 * 1000;
const TERMINAL_PAYMENT_STATUSES = new Set(['paid', 'failed', 'expired']);

export async function fetchCreditsPaymentStatus(paymentId, signal) {
  const res = await api.get(`/credits/status/${encodeURIComponent(paymentId)}`, { signal });
  return res.data;
}

export async function fetchSubscriptionPaymentStatus(paymentId, signal) {
  const res = await api.get(`/subscription/status/${encodeURIComponent(paymentId)}`, { signal });
  return res.data;
}

function isTerminalStatus(status) {
  return TERMINAL_PAYMENT_STATUSES.has(status);
}

function createStatusRefetchInterval({ pollStartedAtRef, expiresAtMs, maxPollMs }) {
  return (query) => {
    const status = query.state.data?.status;
    if (isTerminalStatus(status)) return false;
    if (expiresAtMs && Date.now() >= expiresAtMs) return false;
    if (Date.now() - pollStartedAtRef.current >= maxPollMs) return false;
    return 5000;
  };
}

function usePaymentStatusControl(paymentId, { enabled = true, expiresAt, maxPollMs = PAYMENT_STATUS_MAX_POLL_MS }) {
  const pollStartedAtRef = useRef(Date.now());
  const [timedOut, setTimedOut] = useState(false);

  const expiresAtMs = useMemo(() => {
    if (!expiresAt) return null;
    const parsed = new Date(expiresAt).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }, [expiresAt]);

  useEffect(() => {
    if (!paymentId || !enabled) return undefined;
    pollStartedAtRef.current = Date.now();
    setTimedOut(false);

    const timeoutAt = Math.min(
      pollStartedAtRef.current + maxPollMs,
      expiresAtMs || Number.POSITIVE_INFINITY,
    );
    if (!Number.isFinite(timeoutAt)) return undefined;

    const id = globalThis.setTimeout(() => setTimedOut(true), Math.max(0, timeoutAt - Date.now()));
    return () => globalThis.clearTimeout(id);
  }, [enabled, expiresAtMs, maxPollMs, paymentId]);

  return {
    expiresAtMs,
    maxPollMs,
    pollStartedAtRef,
    queryEnabled: Boolean(paymentId && enabled && !timedOut),
    timedOut,
  };
}

export function useCreditsPaymentStatusQuery(paymentId, options = {}) {
  const { enabled = true, expiresAt, maxPollMs, ...queryOptions } = options;
  const protectedEnabled = useProtectedQueryEnabled(enabled);
  const control = usePaymentStatusControl(paymentId, {
    enabled: protectedEnabled,
    expiresAt,
    maxPollMs,
  });
  const query = useQuery({
    queryKey: queryKeys.creditsPaymentStatus(paymentId),
    queryFn: ({ signal }) => fetchCreditsPaymentStatus(paymentId, signal),
    enabled: control.queryEnabled,
    refetchInterval: createStatusRefetchInterval(control),
    ...queryOptions,
  });

  return {
    ...query,
    isPollingTimedOut: control.timedOut && !isTerminalStatus(query.data?.status),
  };
}

export function useSubscriptionPaymentStatusQuery(paymentId, options = {}) {
  const { enabled = true, expiresAt, maxPollMs, ...queryOptions } = options;
  const protectedEnabled = useProtectedQueryEnabled(enabled);
  const control = usePaymentStatusControl(paymentId, {
    enabled: protectedEnabled,
    expiresAt,
    maxPollMs,
  });
  const query = useQuery({
    queryKey: queryKeys.subscriptionPaymentStatus(paymentId),
    queryFn: ({ signal }) => fetchSubscriptionPaymentStatus(paymentId, signal),
    enabled: control.queryEnabled,
    refetchInterval: createStatusRefetchInterval(control),
    ...queryOptions,
  });

  return {
    ...query,
    isPollingTimedOut: control.timedOut && !isTerminalStatus(query.data?.status),
  };
}
